/**
 * ═══ RECORDINGS THAT SURVIVE THE TAB CLOSING ═══
 *
 * ⚠ THE WHOLE REASON THIS FILE EXISTS: schools have thick walls and thin signal, and a recorder that
 * needs the network is a recorder that loses the one meeting you cannot attend twice. Audio goes to
 * IndexedDB the moment recording stops, and the upload is a separate thing that may happen minutes
 * or hours later, from the car park or from home.
 *
 * ⚠ INDEXEDDB, NOT MEMORY, AND NOT localStorage. In memory it dies with the tab — which on a phone
 * means it dies when you take a call. localStorage is strings only and capped at a few megabytes; a
 * forty-minute meeting is thirty. IndexedDB is the only one of the three that can hold this.
 *
 * ⚠ THE BLOB IS DELETED ONLY AFTER THE SERVER CONFIRMS. A queue that clears on send is a queue that
 * loses a meeting to a 502.
 */

const DB = 'eduvora.recordings';
const STORE = 'pending';
const VERSION = 1;

export interface PendingRecording {
  id: string;              // the server-side recording id
  prospectId: string;
  prospectName: string;
  seconds: number;
  mimeType: string;
  savedAt: number;
  blob: Blob;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  }));
}

export const recordingQueue = {
  put: (r: PendingRecording) => tx('readwrite', (s) => s.put(r)).then(() => undefined),

  /** ⚠ Only after the server has confirmed. Never on send. */
  remove: (id: string) => tx('readwrite', (s) => s.delete(id)).then(() => undefined),

  all: async (): Promise<PendingRecording[]> => {
    try {
      const rows = await tx<PendingRecording[]>('readonly', (s) => s.getAll() as IDBRequest<PendingRecording[]>);
      return rows.sort((a, b) => a.savedAt - b.savedAt);
    } catch {
      // ⚠ A browser with IndexedDB blocked (private mode on some phones) must not break the screen.
      // It just means nothing was queued, and the recorder says so.
      return [];
    }
  },

  get: (id: string) => tx<PendingRecording | undefined>('readonly',
    (s) => s.get(id) as IDBRequest<PendingRecording | undefined>),

  available: () => typeof indexedDB !== 'undefined',
};

/**
 * ⚠ THE LAST LINE OF DEFENCE, AND IT IS NOT MUCH. Browsers only allow a generic "leave site?"
 * prompt, so this cannot explain itself — but a meeting is worth an ugly dialog. The real safety is
 * that the audio is already in IndexedDB by the time this could fire.
 */
export function warnBeforeLeaving(hasPending: () => boolean): () => void {
  const handler = (e: BeforeUnloadEvent) => {
    if (!hasPending()) return;
    e.preventDefault();
    e.returnValue = '';
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}

/** What the browser will actually give us, best first. ⚠ Safari does not do webm. */
export function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  const supported = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  for (const c of candidates) if (supported && MediaRecorder.isTypeSupported(c)) return c;
  return '';
}

/** "webm" / "mp4" — the provider decides how to decode from the extension, so it must match. */
export function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}
